import styled from '@emotion/styled';
import { useMapContext } from '../../hooks/MapProvider';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassMinus } from '@fortawesome/pro-regular-svg-icons';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutoComplete';
import { useContentDetails } from '../../providers';
import { getPageUrl, useAnalyticsClickEvent } from 'rwl-ui';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';

const ZoomOutButton = styled.div`
    display: inline-flex;
    align-items: center;
    gap: var(--rwl-spacing-sm);
    background-color: var(--rwl-palette-white);
    white-space: nowrap;
    padding: 0 var(--rwl-spacing-md);
    border-radius: var(--rwl-radius-lg);
    box-shadow: var(--rwl-shadow-xs);
    border: 1px solid var(--rwl-color-neutral-border);
    font-size: 12px;
    font-weight: 500;
    color: var(--rwl-text-primary);
    cursor: pointer;
    width: fit-content;
    height: 28px;
    &:hover {
        background-color: var(--rwl-palette-gray-100);
    }
`;

const Container = styled.div`
    z-index: 1;
    position: absolute;
    top: var(--rwl-spacing-sm);
    right: var(--rwl-spacing-sm);
`;

const MapControls = ({ map }: { map: google.maps.Map | null }) => {
    const controlDivRef = useRef<HTMLDivElement | null>(null);
    const { neighborhood, setNeighborhood, setOpenMarkerId } = useMapContext();
    const { clearSelectedPlaceId } = usePlacesAutocomplete();
    const { closeDetails } = useContentDetails();
    const sendClickEvent = useAnalyticsClickEvent();

    useEffect(() => {
        if (!map || !neighborhood) return;

        // Create a div to hold the control
        const controlDiv = document.createElement('div');
        controlDivRef.current = controlDiv;

        // Render the React component into the controlDiv
        ReactDOM.render(
            <Container>
                <ZoomOutButton
                    role="button"
                    className="rwl-zoom-out-button"
                    onClick={handleZoomOut}
                >
                    <FontAwesomeIcon
                        icon={faMagnifyingGlassMinus}
                        size="1x"
                        fontSize={16}
                    />
                    Zoom out
                </ZoomOutButton>
            </Container>,
            controlDiv
        );

        // Add the control to the map
        map.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);

        // Cleanup function to remove the control on unmount
        return () => {
            ReactDOM.unmountComponentAtNode(controlDiv);
            const index = map.controls[google.maps.ControlPosition.TOP_RIGHT]
                .getArray()
                .indexOf(controlDiv);
            if (index > -1) {
                map.controls[google.maps.ControlPosition.TOP_RIGHT].removeAt(
                    index
                );
            }
        };
    }, [map, neighborhood]);

    // Function to handle zooming out
    const handleZoomOut = () => {
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.ALL_AREAS,
        });
        setNeighborhood(undefined);
        setOpenMarkerId(undefined);
        clearSelectedPlaceId();
        closeDetails();
    };

    return null; // This component doesn't render anything directly
};

export default MapControls;
